import React, { useMemo } from 'react';
import './Pagination.css'; 
import { left, right } from '../../assets';
import colors from '../../utils/colors'; 

interface PaginationProps {

	currentPage: number;
	totalPages: number;
	onPageChange: React.Dispatch<React.SetStateAction<number>> | null

}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
    const handleChange = (number: number) => {
        if (onPageChange && number >= 1 && number <= totalPages) {
            onPageChange(number);
        }
    };

    const renderPaginationItems = useMemo(() => {
        let items: JSX.Element[] = [];
        const pageWindowSize = 2; 

        const baseButtonClass = "w-8 h-8 flex items-center justify-center transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400 text-sm";
        
        const activeClass = "text-white font-semibold rounded-lg shadow-md"; 
        
        const inactiveClass = "bg-gray-100 text-gray-700 hover:bg-gray-300 rounded-md";
        const ellipsisClass = "w-8 h-8 flex items-center justify-center text-gray-500 px-1";

        const createPageButton = (pageNumber: number) => {
            const isActive = pageNumber === currentPage;
            return (
                <button
                    key={pageNumber}
                    onClick={() => handleChange(pageNumber)}
                    className={`${baseButtonClass} ${isActive ? activeClass : inactiveClass}`}
                    disabled={isActive}
                    style={isActive ? { backgroundColor: colors.primary, borderRadius: '0.5rem' } : {}}
                >
                    {pageNumber}
                </button>
            );
        };

        items.push(createPageButton(1));

        if (currentPage > pageWindowSize + 2) { 
            items.push(<span key="ellipsis-start" className={ellipsisClass}>...</span>);
        }

        let startPage = Math.max(2, currentPage - pageWindowSize);
        let endPage = Math.min(totalPages - 1, currentPage + pageWindowSize);
        
        if (currentPage <= pageWindowSize + 1) { 
            endPage = Math.min(totalPages - 1, 1 + (pageWindowSize * 2));
        }
        if (currentPage >= totalPages - pageWindowSize) { 
            startPage = Math.max(2, totalPages - (pageWindowSize * 2));
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(createPageButton(i));
        }

        if (currentPage < totalPages - (pageWindowSize + 1) ) { 
            items.push(<span key="ellipsis-end" className={ellipsisClass}>...</span>);
        }

        if (totalPages > 1) {
            items.push(createPageButton(totalPages));
        }
        
        items = items.filter((item, index, self) => 
            index === self.findIndex((t) => t.key === item.key)
        );

        return items;
    }, [currentPage, totalPages, onPageChange]);

    if (totalPages <= 1) {
        return null;
    }

    const arrowButtonClass = "w-8 h-8 flex items-center justify-center rounded-md text-gray-600 bg-gray-100 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400";

    return (
        <div className="flex items-center justify-center space-x-1 py-2">
            <button
                onClick={() => handleChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={arrowButtonClass}
                aria-label="Previous Page"
            >
                <img src={left} className="h-3.5 w-3.5" alt="Previous" />
            </button>

            {renderPaginationItems}

            <button
                onClick={() => handleChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={arrowButtonClass}
                aria-label="Next Page"
            >
                <img src={right} className="h-3.5 w-3.5" alt="Next" />
            </button>
        </div>
    );
};

export default Pagination;
